import { useState } from 'react';
import { Alert, Button, Modal, Space, Typography, App as AntApp } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useResetLinkMutation, type User } from './hooks';
import { ErrorAlert } from '@/components/ErrorAlert';

const { Text, Paragraph } = Typography;

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  /** Сотрудник, которому генерируем ссылку. null — модалка закрыта. */
  user: User | null;
}

/**
 * Модалка сброса пароля сотруднику (M7.3-b2, admin-only).
 *
 * Поток симметричен resend-приглашению: подтверждение → генерация ссылки
 * (`POST /admin/users/{id}/reset-link`) → показ одноразовой ссылки со
 * «Скопировать». Письмо НЕ шлётся — владелец передаёт ссылку сотруднику сам.
 */
export function ResetPasswordModal({ open, onClose, user }: ResetPasswordModalProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [resetLink, setResetLink] = useState<string | null>(null);
  const mutation = useResetLinkMutation();

  const handleClose = (): void => {
    setResetLink(null);
    mutation.reset();
    onClose();
  };

  const generate = (): void => {
    if (!user) return;
    mutation.mutate(user.id, {
      onSuccess: (resp) => setResetLink(resp.reset_link),
    });
  };

  // Backend формирует reset_link на EMAIL_BASE_URL; для консистентности с
  // invite-link пересобираем ссылку на текущий origin (валидируется только token).
  const localResetLink = (() => {
    if (!resetLink) return '';
    try {
      const token = new URL(resetLink).searchParams.get('token');
      if (!token) return resetLink;
      return `${window.location.origin}/password-reset?token=${token}`;
    } catch {
      return resetLink;
    }
  })();

  const handleCopy = async (): Promise<void> => {
    if (!resetLink) return;
    try {
      await navigator.clipboard.writeText(localResetLink);
      void message.success(t('common.copied'));
    } catch {
      void message.error(t('errors.generic'));
    }
  };

  return (
    <Modal
      title={t('users.reset_modal.title')}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      footer={
        resetLink ? (
          <Button type="primary" onClick={handleClose}>
            {t('common.close')}
          </Button>
        ) : (
          <Space>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="primary" loading={mutation.isPending} onClick={generate}>
              {t('users.reset_modal.submit')}
            </Button>
          </Space>
        )
      }
    >
      {!resetLink && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            {t('users.reset_modal.confirm', { name: user?.name ?? '', email: user?.email ?? '' })}
          </Paragraph>
          {mutation.error ? <ErrorAlert error={mutation.error} /> : null}
        </Space>
      )}

      {resetLink && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="success"
            showIcon
            message={t('users.reset_modal.success_title')}
            description={t('users.reset_modal.success_description', { name: user?.name ?? '' })}
          />
          <div>
            <Text
              type="secondary"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {t('users.reset_modal.link_label')}
            </Text>
            <div className="sl-deep-link" data-testid="reset-link">
              {localResetLink}
            </div>
          </div>
          <Button icon={<CopyOutlined />} onClick={handleCopy}>
            {t('users.reset_modal.copy')}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('users.reset_modal.hint')}
          </Text>
        </Space>
      )}
    </Modal>
  );
}
